// =============================================================================
// Globals
// =============================================================================

declare global {
    interface Window {
        ssMobileMenuUpdateFocusLoop?: (container: HTMLElement) => void;
    }
}

// =============================================================================
// Constants
// =============================================================================

const MOBILE_MENU_ID = 'ss-header-mobile-menu';
const FOCUSABLE_SELECTOR = 'a.ss-mobile-menu-link, button.ss-mobile-menu-disclosure-button';
const ARROW_NAV_KEYS = new Set(['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Escape']);

// =============================================================================
// Focus helpers
// =============================================================================

/**
 * Get all focusable items inside a .ss-mobile-menu-group whose nearest
 * .ss-mobile-menu-group ancestor is exactly groupEl (i.e. immediate level only).
 */
const getFocusableItemsInGroup = (groupEl: HTMLElement): JQuery => {
    return $(groupEl).find(FOCUSABLE_SELECTOR).filter(function () {
        return $(this).closest('.ss-mobile-menu-group')[0] === groupEl;
    });
};

/**
 * Get all root-level focusable items: those not inside any .ss-mobile-menu-group.
 */
const getRootLevelItems = (mobileMenuEl: HTMLElement): JQuery => {
    return $(mobileMenuEl).find(FOCUSABLE_SELECTOR).filter(function () {
        return $(this).closest('.ss-mobile-menu-group').length === 0;
    });
};

/**
 * Get the focusable items at the same level as el.
 */
const getItemsInSameGroup = (el: HTMLElement, mobileMenuEl: HTMLElement): JQuery => {
    const $group = $(el).closest('.ss-mobile-menu-group');
    if ($group.length) {
        return getFocusableItemsInGroup($group[0] as HTMLElement);
    }
    return getRootLevelItems(mobileMenuEl);
};

// =============================================================================
// Arrow key handlers
// =============================================================================

/**
 * If $button is an expanded disclosure button, move focus into the first
 * item of its controlled group. Returns true if focus moved.
 */
const tryEnterExpandedGroup = ($button: JQuery): boolean => {
    if (!$button.is('.ss-mobile-menu-disclosure-button')) return false;
    if ($button.attr('aria-expanded') !== 'true') return false;
    const controlsId = $button.attr('aria-controls');
    if (!controlsId) return false;
    const groupEl = document.getElementById(controlsId);
    if (!groupEl) return false;
    const $items = getFocusableItemsInGroup(groupEl);
    if (!$items.length) return false;
    ($items[0] as HTMLElement).focus();
    return true;
};

const handleArrowForward = (el: HTMLElement, mobileMenuEl: HTMLElement): void => {
    const $el = $(el);
    if (tryEnterExpandedGroup($el)) return;
    const $items = getItemsInSameGroup(el, mobileMenuEl);
    const idx = $items.index(el);
    if (idx >= 0 && idx < $items.length - 1) {
        ($items[idx + 1] as HTMLElement).focus();
    }
};

const handleArrowBackward = (el: HTMLElement, mobileMenuEl: HTMLElement): void => {
    const $items = getItemsInSameGroup(el, mobileMenuEl);
    const idx = $items.index(el);
    if (idx > 0) {
        ($items[idx - 1] as HTMLElement).focus();
    }
};

const handleHome = (el: HTMLElement, mobileMenuEl: HTMLElement): void => {
    const $items = getItemsInSameGroup(el, mobileMenuEl);
    if ($items.length) ($items[0] as HTMLElement).focus();
};

const handleEnd = (el: HTMLElement, mobileMenuEl: HTMLElement): void => {
    const $items = getItemsInSameGroup(el, mobileMenuEl);
    if ($items.length) ($items[$items.length - 1] as HTMLElement).focus();
};

const handleEscape = (el: HTMLElement, mobileMenuEl: HTMLElement): boolean => {
    const $group = $(el).closest('.ss-mobile-menu-group');
    if (!$group.length) return false;

    const groupId = $group.attr('id');
    const $button = $(mobileMenuEl).find(`.ss-mobile-menu-disclosure-button[aria-controls="${groupId}"]`);
    if (!$button.length) return false;

    $button.removeClass('ss-active').attr('aria-expanded', 'false');
    $button.focus();
    $group.slideUp(function () {
        const container = $button.closest('.modal');
        if (container.length) {
            window.ssMobileMenuUpdateFocusLoop?.(container[0] as HTMLElement);
        }
    });

    return true;
};

// =============================================================================
// Setup
// =============================================================================

const setupMobileMenuKeyboardNav = (): void => {
    const mobileMenuEl = document.getElementById(MOBILE_MENU_ID);
    if (!mobileMenuEl) return;

    mobileMenuEl.addEventListener(
        'keydown',
        (e: KeyboardEvent) => {
            if (!ARROW_NAV_KEYS.has(e.key)) return;

            const el = e.target as HTMLElement;
            const tag = el.tagName.toLowerCase();
            if (tag !== 'a' && tag !== 'button') return;

            switch (e.key) {
                case 'ArrowDown':
                case 'ArrowRight':
                    e.preventDefault();
                    handleArrowForward(el, mobileMenuEl);
                    break;
                case 'ArrowUp':
                case 'ArrowLeft':
                    e.preventDefault();
                    handleArrowBackward(el, mobileMenuEl);
                    break;
                case 'Home':
                    e.preventDefault();
                    handleHome(el, mobileMenuEl);
                    break;
                case 'End':
                    e.preventDefault();
                    handleEnd(el, mobileMenuEl);
                    break;
                case 'Escape': {
                    const handled = handleEscape(el, mobileMenuEl);
                    if (handled) {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                    break;
                }
            }
        },
        true,
    );
};

$(() => {
    setupMobileMenuKeyboardNav();
});
